import React from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Box,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import { DELETE_COMPANY_COMMENT_MUTATION } from '../../pages/clients/gql';
import ConfirmationButton from '../dialogs/ConfirmationButton';
import { dispatchMessage, dispatchException } from 'helper/snackbar';
import { Comment } from './types';
import { getContactSubjectLabel } from './subjects';

interface CommentItemProps {
  comment: Comment;
  onEdit: (comment: Comment) => void;
  onDeleted: () => void;
}

/**
 * Table row component displaying a single comment.
 * Shows creation date, creator, client, description, contact type, and relevance badges.
 * Supports edit and delete actions.
 */
const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onEdit,
  onDeleted
}) => {
  const dispatch = useDispatch();

  const [deleteCommentMutation] = useMutation(DELETE_COMPANY_COMMENT_MUTATION);

  const handleEdit = () => {
    onEdit(comment);
  };

  const handleDelete = async () => {
    try {
      await deleteCommentMutation({
        variables: { id: comment.id },
      });
      dispatchMessage(dispatch, i18next.t('publiccompany-comment-delete-success'));
      onDeleted();
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18next.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) + ' ' + date.toLocaleTimeString(i18next.language, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCommentType = (val?: string | null) => {
    if (!val) return '-';
    if (val === 'PERS') return String(i18next.t('comment-contact-type-personal'));
    if (val === 'EMAIL') return String(i18next.t('comment-contact-type-email'));
    if (val === 'TEL') return String(i18next.t('comment-contact-type-phone'));
    if (val === 'INFO') return String(i18next.t('comment-contact-type-info'));
    return val;
  };

  return (
    <TableRow hover>
      {/* Erstellt am */}
      <TableCell>
        <Box display="flex" flexDirection="column">
          <Typography variant="body2">
            {formatDate(comment.createdAt)}
          </Typography>
          {comment.updatedAt !== comment.createdAt && (
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              {i18next.t('comment-edited-at')}: {formatDate(comment.updatedAt)}
            </Typography>
          )}
        </Box>
      </TableCell>

      {/* Ersteller */}
      <TableCell>
        <Typography variant="body2">
          {comment.createdBy?.name || i18next.t('comment-dialog-unknown-user')}
        </Typography>
      </TableCell>

      {/* Ansprechperson */}
      <TableCell>
        {comment.publicClient ? (
          <Typography variant="body2">
            {`${comment.publicClient.firstname || ''} ${comment.publicClient.lastname || ''}`.trim()}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )}
      </TableCell>

      {/* Beschreibung */}
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxWidth: '300px'
          }}
        >
          {comment.description}
        </Typography>
      </TableCell>

      {/* Kontaktart */}
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxWidth: '300px'
          }}
        >
          {formatCommentType(comment.contactType)}
        </Typography>
      </TableCell>

      {/* Relevanz */}
      <TableCell align="center">
        <Box display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
          {comment.isBookingRelevant && (
            <Tooltip title={String(i18next.t('comment-booking-relevant'))}>
              <Chip
                icon={<WarningIcon />}
                label={String(i18next.t('comment-booking'))}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Tooltip>
          )}
          {comment.isContactRelevant && (
            <Tooltip title={String(i18next.t('comment-contact-relevant'))}>
              <Chip
                label={getContactSubjectLabel(comment.contactSubject)}
                size="small"
                color="info"
                variant="outlined"
              />
            </Tooltip>
          )}
          {!comment.isBookingRelevant && !comment.isContactRelevant && (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </Box>
      </TableCell>

      {/* Aktionen */}
      <TableCell align="center">
        <Box
          display="flex"
          gap={0.5}
          justifyContent="center"
        >
          <Tooltip title={i18next.t('publiccompany-comment-edit')}>
            <IconButton
              size="small"
              onClick={handleEdit}
              sx={{ color: 'primary.main' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <ConfirmationButton
            confirmationTitle={i18next.t('publiccompany-comment-delete')}
            confirmationQuestion={i18next.t('publiccompany-comment-delete-confirm')}
            onConfirm={handleDelete}
            icon={true}
            disabled={false}
          >
            <Tooltip title={i18next.t('publiccompany-comment-delete')}>
              <DeleteIcon fontSize="small" />
            </Tooltip>
          </ConfirmationButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default CommentItem;
